import { namespace } from '@common/config/constants';
import { KIND_GLYPH, type GlyphKind } from '@common/components/kind-glyphs/kind-glyphs';
import DestinationAvatars, { DestinationPopover, useDestinationPopover, type DestinationHints } from '@common/components/destination-avatars/destination-avatars';
import type { DestinationPlan } from '@common/components/destination-avatars/destination-plan';
import { formatInLanguage, relativeInLanguage } from '@common/config/moment-locales';
import { CalendarPlus, LocateFixed, Repeat } from 'lucide-react';
import moment from 'moment';
import React, { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './planner-list-row.module.scss';

export type PlannerRowTone = 'neutral' | 'brand' | 'ok' | 'warn' | 'bad';

export interface PlannerListRowProps {
    /** Identity colour — the same one this item's chip carries on the grid. */
    railColor: string;
    /** What the send IS. Secondary on this row: the bottom-end corner badge
     *  on the destination, the same glyph its calendar chip carries. */
    kind: GlyphKind;
    /**
     * WHERE the send goes — the row's leading circle. One person's avatar, an
     * overlapping group for a campaign or a multi-recipient send, the account's
     * own Status for a Status. Decided by `planDestination` so this row and the
     * calendar hover card draw the same thing.
     */
    destination: DestinationPlan;
    /** Per-recipient name and picture the document already carries. */
    destinationHints?: DestinationHints;
    /** Contact name, or the campaign's name. */
    title: string;
    /** The message itself, or whatever one line best describes the item. */
    preview?: string;
    /** Small fact about the item itself ("6 contacts"). Leads the preview line:
     *  on the time line it pushed the exact stamp out of a 290px sidebar. */
    detail?: string;
    dueDate?: string | Date | number;
    status?: { label: string; tone: PlannerRowTone };
    isRecurring?: boolean;
    /** Rendered under the meta line — a running campaign's progress bar. */
    extra?: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    /** "Show on calendar": move the grid to this item and flash its chip.
     *  The row itself opens the item, so this is its only secondary action. */
    onReveal?: () => void;
    /** FE#444 — a sent send can be run again: hover action that opens "Schedule again" for this row. */
    onScheduleAgain?: () => void;
    /** FE#444 — select mode: a checkbox leads the row (the caller makes the row's click toggle it). */
    selection?: { checked: boolean; onToggle: () => void };
}

const TONE_CLASS: Record<PlannerRowTone, string> = {
    neutral: styles.pillNeutral,
    brand: styles.pillBrand,
    ok: styles.pillOk,
    warn: styles.pillWarn,
    bad: styles.pillBad,
};

/** First letter of the name, for a row with no recipient to draw. */
const initialOf = (name?: string) => (name ?? '').trim().charAt(0).toUpperCase() || '?';

/** How the kind glyph is announced. Names the same kinds with the same words
 *  the calendar hover card uses. */
const KIND_LABEL: Record<GlyphKind, { labelKey: string }> = {
    message: { labelKey: 'btgpt.widgets.messageTypes.message' },
    poll: { labelKey: 'btgpt.widgets.messageTypes.poll' },
    campaign: { labelKey: 'calendar.campaign' },
    status: { labelKey: 'calendar.grid.statusKind' },
};

/**
 * One row of the planner's sidebar.
 *
 * It exists because both sidebar rows used to render through
 * `WhatsappContactBox` — the box built for WhatsApp's own contact list — which
 * gave the planner a list from a different product: 49px circles, 16px names, a
 * fixed 72px frame the content overflowed sideways out of, and (on campaigns) a
 * raw Mongo id printed next to the name. This is the calendar's own vocabulary
 * instead: the identity rail from the chips, the same type scale, the relative
 * time the grid already speaks in.
 */
export const PlannerListRow: React.FC<PlannerListRowProps> = ({
    railColor, kind, destination, destinationHints, title, preview, detail, dueDate, status,
    isRecurring, extra, onClick, onReveal, onScheduleAgain, selection,
}) => {
    const { t, i18n } = useTranslation(namespace);
    const KindGlyph = KIND_GLYPH[kind];

    // WHO it goes to, on demand: the member list opens on hover of the slot and
    // on KEYBOARD focus of the row (the row is the focusable thing — a second
    // focus stop nested inside a button would be an interactive element inside
    // an interactive element). Nothing is mounted until it opens, so scrolling
    // the virtualised list never renders or fetches a list.
    const slotRef = useRef<HTMLDivElement>(null);
    const popoverId = useId();
    const list = useDestinationPopover(destination.mode !== 'none');
    const kindLabel = t(KIND_LABEL[kind].labelKey);

    const due = dueDate == null ? null : moment(dueDate);
    const valid = due?.isValid() ? due : null;
    const isPast = valid ? valid.valueOf() < Date.now() : false;

    return (
        <div
            className={selection ? `${styles.row} ${styles.rowSelecting}` : styles.row}
            style={{ ['--row-rail' as any]: railColor }}
            role="button"
            tabIndex={0}
            aria-describedby={list.open ? popoverId : undefined}
            onClick={e => { list.close(); onClick(e); }}
            // Keyboard focus of the ROW opens the list: the row is the focusable
            // thing here, and a second focus stop nested inside a button would be
            // an interactive element inside an interactive element.
            onFocus={list.focusHandlers.onFocus}
            onBlur={list.focusHandlers.onBlur}
            onKeyDown={e => {
                list.focusHandlers.onKeyDown(e);
                if (e.isPropagationStopped()) return;
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                list.close();
                onClick(e as unknown as React.MouseEvent);
            }}
        >
            <span className={styles.rail} aria-hidden />

            {selection && (
                <input
                    type="checkbox"
                    className={styles.selectBox}
                    checked={selection.checked}
                    aria-label={t('scheduleAgain.selectRow', { defaultValue: 'Select {{title}}', title })}
                    onChange={selection.onToggle}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                />
            )}

            {/* The leading slot answers "to whom" — the owner's first question
                of this list — and has ONE footprint on every row, so the titles
                line up. It is an avatar with two corner badges on its END edge:
                the count at the top when there is more than one recipient, and
                what the send IS at the bottom, always — the same glyph its
                calendar chip carries. */}
            <div ref={slotRef} className={styles.destination} {...list.hoverHandlers}>
                {destination.mode === 'none' ? (
                    <>
                        <div className={styles.avatar}><span>{initialOf(title)}</span></div>
                        <span className={styles.kindBadge}>
                            <KindGlyph className={styles.kindGlyph} role="img" aria-label={kindLabel} />
                        </span>
                    </>
                ) : (
                    <DestinationAvatars plan={destination} size="row" hints={destinationHints} kindBadge={{ kind, label: kindLabel }} />
                )}
            </div>
            {list.open && slotRef.current && (
                <DestinationPopover id={popoverId} anchor={slotRef.current} plan={destination} hints={destinationHints} />
            )}

            <div className={styles.content}>
                <div className={styles.titleLine}>
                    {/* The name is the row's identity, so it is not cut off with an
                        ellipsis and recovered from an OS tooltip — it wraps to a second
                        line instead. A native `title` was doing that job here; it is
                        gone everywhere else in the planner, and it never solved this
                        for touch or keyboard users anyway. */}
                    <span className={styles.title}>{title}</span>
                    {isRecurring && (
                        <span
                            className={styles.recurringMark}
                            role="img"
                            aria-label={t('calendar.grid.repeats', 'Repeats')}
                        >
                            <Repeat size={12} strokeWidth={2.2} />
                        </span>
                    )}
                </div>

                {/* No `title` here either: the preview is a preview by design — the
                    row opens the item, which is where the whole message belongs. The
                    full text is still in the row's accessible name, which a button
                    role computes from its contents. */}
                {(preview || detail) && (
                    <div className={styles.preview}>
                        {detail && (
                            // The separator belongs to the pair, not to the
                            // detail: an unnamed campaign has no preview, and a
                            // lone "6 contacts ·" trails a dot into nothing.
                            <span className={preview ? styles.detailBefore : undefined}>{detail}</span>
                        )}
                        {preview}
                    </div>
                )}

                <div className={styles.metaLine}>
                    {valid && (
                        <span className={`${styles.when} ${isPast ? styles.whenPast : ''}`}>
                            <span className={styles.whenRelative}>{relativeInLanguage(valid.valueOf(), i18n.language)}</span>
                            <span className={styles.whenExact}>
                                {formatInLanguage(valid.valueOf(), i18n.language, 'HH:mm · D MMM')}
                            </span>
                        </span>
                    )}

                    {status && (
                        <span className={`${styles.pill} ${TONE_CLASS[status.tone]}`}>
                            <span className={styles.pillLabel}>{status.label}</span>
                        </span>
                    )}
                </div>

                {extra && <div className={styles.extra}>{extra}</div>}
            </div>

            {/* Hover-only, where the three-dot menu used to be: clicking the row
                opens the item, so the menu had nothing left to offer. This finds
                the item ON the calendar beside the list. Its own click must not
                reach the row, or the row would open the item too. */}
            {onScheduleAgain && (
                <button
                    type="button"
                    className={`${styles.revealButton} ${styles.secondAction}`}
                    aria-label={t('scheduleAgain.action', { defaultValue: 'Schedule again' })}
                    data-tip={t('scheduleAgain.action', { defaultValue: 'Schedule again' })}
                    onClick={e => { e.stopPropagation(); list.close(); onScheduleAgain(); }}
                    onKeyDown={e => e.stopPropagation()}
                >
                    <CalendarPlus size={15} />
                </button>
            )}
            {onReveal && (
                <button
                    type="button"
                    className={styles.revealButton}
                    aria-label={t('calendar.sidebar.showOnCalendar', 'Show on calendar')}
                    data-tip={t('calendar.sidebar.showOnCalendar', 'Show on calendar')}
                    onClick={e => { e.stopPropagation(); list.close(); onReveal(); }}
                    onKeyDown={e => e.stopPropagation()}
                >
                    <LocateFixed size={15} />
                </button>
            )}
        </div>
    );
};

export default PlannerListRow;
